import { useEffect, useMemo, useContext, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { Flex } from "uikit-react";
import classnames from "classnames";
import { AppContexts } from "../../../providers";
import Pagination from "../Pagination";
import TagsList from "./TagsList";
import RecordsPerPage from "../RecordsPerPage";
import UIkit from "uikit";
import { useCookies } from "react-cookie";
import TableLoading from "../Loading/table_loading";
import { useDeepCompareEffect } from "../../../hook";
import { Link } from "../../../ui";
import SearchableDropdown from "../SearchableDropdown";
import { usePermissionsStore } from "../../permissions/permissions_store";
import { tagFilterOptions, tagsMatchGroup } from "../../../utils/tag-groups";

// Shared list page for displaying rentals (units or property guides)
const getDefaultCriteria = (hasFilters, filterOptions) => ({
  search_term: "",
  sort: "updated_at",
  page: 0,
  tag: "all",
  tag_group: "all",
  ...(hasFilters && { filter: Object.keys(filterOptions)[0] }),
});

const RentalListPage = ({ config = {} }) => {
  const {
    pageTitle = "Rentals",
    addButtonLabel = "Rental",
    emptyMessage = "No rentals found.",
    sortOptions = ["updated_at"],
    hasFilters = false,
    filterOptions = {},
    extraButtons = [],
    ShowShareComponent = false,
    ShowMonitoringComponent = false,
    AddComponent,
    RowComponent,
    ShareComponent,
    editModuleKey = null,
    MonitoringComponent,
  } = config;

  const { canEdit } = usePermissionsStore();
  const canManage = editModuleKey ? canEdit(editModuleKey) : true;

  const defaultCriteria = getDefaultCriteria(hasFilters, filterOptions);

  const parseQueryString = (queryString) => {
    return {
      ...defaultCriteria,
      ...Object.fromEntries(new URLSearchParams(queryString)),
    };
  };

  const {
    loading,
    get,
    setIsNew,
    rentals,
    selectedFlags,
    setSelectedFlags,
    selectedRentals,
    setSelectedRentals,
    updateRental,
  } = useContext(AppContexts.RentalsContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const destinationContext = useContext(AppContexts.DestinationContext);
  const authContext = useContext(AppContexts.AuthContext);
  const columnCount = +authContext.user?.user?.owner_id ? 4 : 5;
  const zoneContext = useContext(AppContexts.ZonesContext);

  const userType = authContext.user?.details?.type;
  const [cookies] = useCookies(["records-per-page"]);
  const history = useHistory();
  const location = useLocation();
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const [criteria, setCriteria] = useState(() => {
    return location.search
      ? parseQueryString(location.search)
      : defaultCriteria;
  });

  useEffect(() => {
    document.title = `${pageTitle} - RueBaRue`;
    get();
    settingsContext.get(["tags"]);
    destinationContext.get();
    zoneContext.getZoneGroups(["rentals"]);
  }, []);

  const listGuides = useMemo(() => {
    if (!rentals) {
      return [];
    } else {
      return rentals.map((rental) => {
        let destinationTag = rental?.tags
          ?.filter((item) => item.type === "destination")
          .sort((prev, next) => prev.name.localeCompare(next.name));
        let rentalTag = rental?.tags
          ?.filter((tag) => tag.type !== "destination")
          .sort((prev, next) => prev.name.localeCompare(next.name));
        let filteredRentalTag = rentalTag?.filter((item) => {
          if (!destinationTag.find((tag) => tag.name === item.name)) {
            return true;
          }
          return false;
        });
        filteredRentalTag?.forEach((item) => {
          destinationTag.push(item);
        });
        return { ...rental, tags: destinationTag };
      });
    }
  }, [rentals]);

  const handleSelectAll = useCallback(
    (event) => {
      setSelectedFlags((flags) => flags.map(() => event.target.checked));
    },
    [selectedFlags],
  );

  const filteredGuides = useMemo(() => {
    let filtered = listGuides.filter((guide) => {
      // Tag filter
      if (criteria.tag && criteria.tag != "all") {
        if (
          !guide.tags ||
          guide.tags.reduce(
            (passed, tag) => passed || tag.name === criteria.tag,
            false,
          ) === false
        ) {
          return false;
        }
      }

      // Tag group filter. This is additive to the existing tag filter, so when
      // both are selected a property must match both criteria.
      if (
        criteria.tag_group &&
        criteria.tag_group !== "all" &&
        !tagsMatchGroup(
          guide.tags,
          settingsContext.settings?.tag_groups,
          criteria.tag_group,
        )
      ) {
        return false;
      }

      // Search filter
      if (criteria.search_term) {
        const search = criteria.search_term.trim().toLowerCase();

        if (
          !guide.pms_id?.toLowerCase().includes(search) &&
          !guide.name?.toLowerCase().includes(search) &&
          !guide.address?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }

      // Custom filter (for property_guide)
      if (hasFilters && criteria.filter) {
        if (criteria.filter === Object.keys(filterOptions)[0]) {
          // First filter option - no filtering needed
          return true;
        } else if (criteria.filter === "mis_destination_id") {
          return !Number(guide.destination_id);
        } else if (criteria.filter === "pms_id") {
          return !guide.pms_id;
        }
      }

      return true;
    });

    // Sort
    return filtered.sort((prev, next) => {
      if (criteria.sort === "updated_at")
        return next[criteria.sort].localeCompare(prev[criteria.sort]);
      if (criteria.sort === "name")
        return prev[criteria.sort].localeCompare(next[criteria.sort]);
      if (criteria.sort === "pms_id")
        return prev[criteria.sort].localeCompare(next[criteria.sort]);
    });
  }, [listGuides, criteria, hasFilters, settingsContext.settings?.tag_groups]);

  const pageGuides = useMemo(
    () =>
      filteredGuides.slice(
        recordsPerPage * criteria.page,
        recordsPerPage * (criteria.page + 1),
      ),
    [filteredGuides, recordsPerPage, criteria.page],
  );

  useEffect(() => {
    setSelectedFlags(Array(filteredGuides.length).fill(false));
  }, [filteredGuides.length]);

  const selectedAll = useMemo(
    () => selectedFlags.every((checked) => checked),
    [selectedFlags],
  );

  const selectedAny = useMemo(
    () => selectedFlags.some((checked) => checked),
    [selectedFlags],
  );

  useEffect(() => {
    setSelectedRentals(
      selectedFlags
        .map((selected, index) => (selected ? filteredGuides[index] : null))
        .filter((contact) => contact),
    );
  }, [selectedFlags, filteredGuides]);

  const allTags = useMemo(() => {
    if (!settingsContext.settings) {
      return [];
    } else {
      if (!settingsContext.settings.tags) {
        return [];
      } else {
        return settingsContext.settings.tags.sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      }
    }
  }, [settingsContext.settings]);

  const tagGroups = useMemo(
    () => settingsContext.settings?.tag_groups || [],
    [settingsContext.settings?.tag_groups],
  );

  const availableTagOptions = useMemo(
    () =>
      tagFilterOptions(
        allTags,
        tagGroups,
        criteria.tag_group,
        (tag) => tag.name,
      ),
    [allTags, tagGroups, criteria.tag_group],
  );

  useEffect(() => {
    if (
      !settingsContext.loading &&
      settingsContext.settings?.tags &&
      criteria.tag !== "all" &&
      !availableTagOptions.some((option) => option.value === criteria.tag)
    ) {
      setCriteria((previous) => ({ ...previous, tag: "all" }));
    }
  }, [
    availableTagOptions,
    criteria.tag,
    settingsContext.loading,
    settingsContext.settings?.tags,
  ]);

  const handleZoneToggle = async (response) => {
    // console.log(response);
    if (response.error_code === "EMPTY_ZONE") {
      UIkit.notification(
        "Please add at least one user to this zone before adding this property",
        "error",
      );
    } else {
      await zoneContext.getZoneGroups(["rentals"]);
    }
  };

  const handleIndexChanged = (newPageIndex) => {
    handleCriteriaChange("page", newPageIndex);
  };

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }, []);

  useDeepCompareEffect(() => {
    const searchParams = new URLSearchParams(criteria).toString();
    history.replace(`${location.pathname}?${searchParams}`);
  }, [criteria]);

  return (
    <>
      <section id="property-guide-template">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">{pageTitle}</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              {canManage && (
                <button
                  className="uk-button hms-btn hms-red-btn"
                  uk-toggle="target: #addPropertyGuide-modal"
                  onClick={() => setIsNew(true)}
                >
                  <span uk-icon="icon: plus; ratio: 0.7"></span> {addButtonLabel}
                </button>
              )}
              {canManage && (
                <TagsList
                  all={allTags}
                  disabled={!selectedAny}
                  selected={selectedRentals}
                  updateItem={(rental) => updateRental(rental.rental_id, rental)}
                />
              )}
              {extraButtons.map((btn, idx) => (
                <button
                  key={idx}
                  className="uk-button hms-btn hms-white-btn"
                  onClick={() => history.push(btn.href)}
                >
                  {btn.label}
                </button>
              ))}
              <div data-uk-dropdown="mode: click">
                <ul className="uk-nav uk-dropdown-nav tags-selector">
                  {allTags?.map((tagValue, index) => (
                    <li key={index}>
                      <label>
                        <input className="uk-checkbox" type="checkbox" />
                        {tagValue.name}
                      </label>
                    </li>
                  ))}
                </ul>
                <hr />
                <a href="/settings/tags">Manage Tags</a>
              </div>
            </div>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card">
            <div>
              <div>
                <div className="uk-form uk-flex uk-flex-between uk-flex-top guest-form-top-filters">
                  <div className="hms-form">
                    {hasFilters && (
                      <div className="uk-inline">
                        <div
                          uk-form-custom="target: > * > span:first-child"
                          id="sort"
                        >
                          <select
                            value={criteria.filter}
                            onChange={(e) =>
                              handleCriteriaChange("filter", e.target.value)
                            }
                            className="uk-margin-small-right"
                          >
                            {Object.entries(filterOptions).map(
                              ([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              ),
                            )}
                          </select>
                          <button
                            className="uk-button uk-button-default"
                            type="button"
                            tabIndex="-1"
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    )}

                    {!settingsContext.loading && tagGroups.length > 0 && (
                      <SearchableDropdown
                        value={criteria.tag_group}
                        onChange={(val) =>
                          handleCriteriaChange("tag_group", val)
                        }
                        placeholder="All Tag Groups"
                        options={[
                          { value: "all", label: "All Tag Groups" },
                          ...tagGroups.map((group) => ({
                            value: String(group.id),
                            label: group.label,
                          })),
                        ]}
                      />
                    )}

                    {!settingsContext.loading && (
                      <SearchableDropdown
                        value={criteria.tag}
                        onChange={(val) => handleCriteriaChange("tag", val)}
                        placeholder="All Tags"
                        options={[
                          { value: "all", label: "All Tags" },
                          ...availableTagOptions,
                        ]}
                      />
                    )}

                    <div className="uk-inline hms-search-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() => handleCriteriaChange("search_term", "")}
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Property ID or Address"
                          value={criteria.search_term}
                          onChange={(e) =>
                            handleCriteriaChange(
                              "search_term",
                              e.target.value.toLowerCase(),
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div>
                      <div className="hms-sortby-btn">
                        <button className="uk-button" type="button">
                          SORT BY
                          <span uk-icon="icon: triangle-down"></span>
                        </button>
                        <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                          <ul className="uk-nav uk-dropdown-nav">
                            {sortOptions.map((option) => (
                              <li
                                key={option}
                                className={classnames({
                                  "uk-active": criteria.sort === option,
                                })}
                              >
                                <Link
                                  href="#"
                                  onClick={() =>
                                    handleCriteriaChange("sort", option)
                                  }
                                >
                                  {option === "updated_at"
                                    ? "Last Updated"
                                    : option.charAt(0).toUpperCase() +
                                      option.slice(1)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="select-item">
                      <div className="autosend-checkbox">
                        <label htmlFor="propertyCBAll">
                          <input
                            type="checkbox"
                            id="propertyCBAll"
                            checked={selectedAll}
                            onChange={handleSelectAll}
                          />
                        </label>
                      </div>
                    </th>
                    <th>Property ID/Address</th>
                    <th className="uk-table-expand">TAGS</th>
                    {!+authContext.user?.user?.owner_id && (
                      <th className="uk-table-expand">
                        ZONES
                        <img
                          className="info-icon"
                          src="/images/info-icon.svg"
                          uk-tooltip="Restrict access to specific properties and messages."
                        />
                      </th>
                    )}
                    <th className="actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    TableLoading(columnCount)
                  ) : pageGuides.length > 0 ? (
                    pageGuides.map((guide, index) => (
                      <RowComponent
                        guide={guide}
                        index={recordsPerPage * Number(criteria.page) + index}
                        allTags={allTags}
                        key={guide.rental_id || index}
                        onZoneToggle={handleZoneToggle}
                        canManage={canManage}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columnCount} style={{ padding: "30px 25px" }}>
                        {emptyMessage}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={filteredGuides.length}
              index={Number(criteria.page)}
              onIndexChanged={handleIndexChanged}
            />
            {filteredGuides.length > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>

      {canManage && <AddComponent />}
      {ShowShareComponent && <ShareComponent />}
      {ShowMonitoringComponent && MonitoringComponent && <MonitoringComponent />}
    </>
  );
};

export default RentalListPage;
